/**
 * LocalStorage abstraction layer.
 *
 * Provides typed getter/setter functions for every value the application
 * persists to `localStorage`.  All raw key strings are encapsulated here
 * so they are never duplicated across the codebase.
 *
 * ### Stored keys
 * | Key | Contents |
 * |-----|----------|
 * | `access_token` | JWT access token |
 * | `refresh_token` | JWT refresh token |
 * | `theme_color` | `"light"` or `"dark"` |
 * | `marketview_rm` | Reversibly encoded Remember Me credentials |
 * | `marketview_chart_layout` | Serialised `SavedChartLayout` |
 * | `marketview_drawings_<sym>_<tf>` | Serialised `Drawing[]` per symbol+timeframe |
 * | `marketview_chart_settings` | Serialised chart appearance settings |
 */

/** Return the stored JWT access token, or `null` if absent. */
export const getAccessToken = () => {
  return localStorage.getItem("access_token");
};

/** Return the stored JWT refresh token, or `null` if absent. */
export const getRefreshToken = () => {
  return localStorage.getItem("refresh_token");
};

/** Persist the JWT access token to `localStorage`. */
export const setAccessToken = (accessToken: string) => {
  return localStorage.setItem("access_token", accessToken);
};

/** Persist the JWT refresh token to `localStorage`. */
export const setRefreshToken = (refreshToken: string) => {
  return localStorage.setItem("refresh_token", refreshToken);
};

/** Remove the access token from `localStorage` (called on logout/refresh failure). */
export const removeAccessToken = () => {
  return localStorage.removeItem("access_token");
};

/** Remove the refresh token from `localStorage`. */
export const removeRefreshToken = () => {
  return localStorage.removeItem("refresh_token");
};

export const getColorTheme = () => {
  return localStorage.getItem("theme_color");
};

export const setColorTheme = (colorTheme: string) => {
  return localStorage.setItem("theme_color", colorTheme);
};

const REMEMBER_ME_KEY = "marketview_rm";
const REMEMBER_ME_KEYS = ["3u$oD#kL9@mP", "Xp@9mNqRv!7c", "vF!7cWzY3u$o"];

const xorBytes = (bytes: Uint8Array, key: string): Uint8Array => {
  const keyBytes = new TextEncoder().encode(key);
  return bytes.map((byte, index) => byte ^ keyBytes[index % keyBytes.length]);
};

const toBase64 = (bytes: Uint8Array): string => {
  let binary = "";
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary);
};

const fromBase64 = (value: string): Uint8Array =>
  Uint8Array.from(atob(value), (character) => character.charCodeAt(0));

const encryptRememberedCredentials = (credentials: RememberedCredentials): string => {
  const encoder = new TextEncoder();
  let bytes = xorBytes(encoder.encode(JSON.stringify(credentials)), REMEMBER_ME_KEYS[0]);
  bytes = xorBytes(encoder.encode(toBase64(bytes)), REMEMBER_ME_KEYS[1]);
  return toBase64(xorBytes(encoder.encode(toBase64(bytes)).slice().reverse(), REMEMBER_ME_KEYS[2]));
};

const decryptRememberedCredentials = (cipher: string): RememberedCredentials => {
  const decoder = new TextDecoder();
  let bytes = xorBytes(fromBase64(cipher), REMEMBER_ME_KEYS[2]);
  const layerTwo = decoder.decode(bytes.slice().reverse());
  bytes = xorBytes(fromBase64(layerTwo), REMEMBER_ME_KEYS[1]);
  const layerOne = decoder.decode(bytes);
  bytes = xorBytes(fromBase64(layerOne), REMEMBER_ME_KEYS[0]);
  return JSON.parse(decoder.decode(bytes));
};

export interface RememberedCredentials {
  username: string;
  password: string;
}

export const setRememberedCredentials = (credentials: RememberedCredentials) => {
  localStorage.setItem(REMEMBER_ME_KEY, encryptRememberedCredentials(credentials));
  localStorage.removeItem("remembered_username");
  localStorage.removeItem("session");
};

export const getRememberedCredentials = (): RememberedCredentials => {
  localStorage.removeItem("remembered_username");
  localStorage.removeItem("session");
  try {
    const cipher = localStorage.getItem(REMEMBER_ME_KEY);
    return cipher ? decryptRememberedCredentials(cipher) : { username: "", password: "" };
  } catch {
    localStorage.removeItem(REMEMBER_ME_KEY);
    return { username: "", password: "" };
  }
};

export const removeRememberedCredentials = () => {
  localStorage.removeItem(REMEMBER_ME_KEY);
  localStorage.removeItem("remembered_username");
  localStorage.removeItem("session");
};

// ── Auto-update release notes ─────────────────────────────────────

export interface PendingReleaseNotes {
  version: string;
  notes: string;
}

export const setPendingReleaseNotes = (data: PendingReleaseNotes) => {
  localStorage.setItem("pending_release_notes", JSON.stringify(data));
};

export const getPendingReleaseNotes = (): PendingReleaseNotes | null => {
  const raw = localStorage.getItem("pending_release_notes");
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PendingReleaseNotes;
  } catch {
    return null;
  }
};

export const clearPendingReleaseNotes = () => {
  localStorage.removeItem("pending_release_notes");
};

// ── Chart Layout Persistence ──────────────────────────────────────

export interface SavedPanelConfig {
  id: string;
  symbol: { symbol: string; segment?: string; name?: string; id?: number } | null;
  timeframe: string;
  chartType?: string;
}

export interface SavedChartLayout {
  panels: SavedPanelConfig[];
  layoutMode: string;
  activeTabId: string | null;
  marketWatchOpen: boolean;
  toolsSidebarOpen?: boolean;
}

const CHART_LAYOUT_KEY = "marketview_chart_layout";

/** Persist the chart panel layout to `localStorage` as serialised JSON. */
export const saveChartLayout = (layout: SavedChartLayout) => {
  try {
    localStorage.setItem(CHART_LAYOUT_KEY, JSON.stringify(layout));
  } catch (e) {
    console.warn("[Storage] Failed to save chart layout:", e);
  }
};

/** Load the previously saved chart layout, or `null` on a first run / parse error. */
export const loadChartLayout = (): SavedChartLayout | null => {
  try {
    const raw = localStorage.getItem(CHART_LAYOUT_KEY);
    if (!raw) return null;
    const layout = JSON.parse(raw) as SavedChartLayout;
    // Migrate: replace any "1D" default timeframe saved from the old default
    // so panels open in 1-minute (intraday) by default from now on.
    if (layout?.panels) {
      layout.panels = layout.panels.map(p => ({
        ...p,
        timeframe: p.timeframe === "1D" ? "1" : p.timeframe,
      }));
    }
    return layout;
  } catch (e) {
    console.warn("[Storage] Failed to load chart layout:", e);
    return null;
  }
};

/** Remove the chart layout from `localStorage` (used when the user resets the workspace). */
export const clearChartLayout = () => {
  localStorage.removeItem(CHART_LAYOUT_KEY);
};

// ── Drawing Persistence ──────────────────────────────────────────

const DRAWINGS_KEY_PREFIX = "marketview_drawings_";

/**
 * Persist drawing objects for a specific symbol+timeframe combination.
 *
 * @param symbolKey - Symbol string, e.g. `"NIFTY"`.
 * @param timeframe - Timeframe string, e.g. `"5"`.
 * @param drawings  - Array of drawing objects to persist.
 *                    Passing an empty array removes the stored key.
 */
export const saveDrawings = (symbolKey: string, timeframe: string, drawings: any[]) => {
  try {
    const key = `${DRAWINGS_KEY_PREFIX}${symbolKey}_${timeframe}`;
    if (drawings.length === 0) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(drawings));
    }
  } catch (e) {
    console.warn("[Storage] Failed to save drawings:", e);
  }
};

/**
 * Load persisted drawings for a symbol+timeframe combination.
 *
 * @returns The saved drawing array, or `[]` on first use / parse error.
 */
export const loadDrawings = (symbolKey: string, timeframe: string): any[] => {
  try {
    const key = `${DRAWINGS_KEY_PREFIX}${symbolKey}_${timeframe}`;
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch (e) {
    console.warn("[Storage] Failed to load drawings:", e);
    return [];
  }
};

export const clearAllDrawings = () => {
  const keys = Object.keys(localStorage).filter(k => k.startsWith(DRAWINGS_KEY_PREFIX));
  keys.forEach(k => localStorage.removeItem(k));
};

// ── Custom Timeframes Persistence ───────────────────────────────

const CUSTOM_TIMEFRAMES_KEY = "marketview_custom_timeframes";

export interface CustomTimeframe {
  key: string;      // unique identifier (e.g. "2", "45", "2H")
  minutes: number;  // duration in minutes
  label: string;    // display label (e.g. "2m", "45m", "2H")
}

export const loadCustomTimeframes = (): CustomTimeframe[] => {
  try {
    const raw = localStorage.getItem(CUSTOM_TIMEFRAMES_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
};

export const saveCustomTimeframes = (timeframes: CustomTimeframe[]) => {
  try {
    localStorage.setItem(CUSTOM_TIMEFRAMES_KEY, JSON.stringify(timeframes));
  } catch (e) {
    console.warn("[Storage] Failed to save custom timeframes:", e);
  }
};

// ── Chart Settings Persistence ──────────────────────────────────

const CHART_SETTINGS_KEY = "marketview_chart_settings";

export interface SavedChartSettings {
  upColor: string;
  downColor: string;
  gridVisible: boolean;
  crosshairMode: number; // 0=Normal, 1=Magnet
  priceScalePosition: "right" | "left";
}

const DEFAULT_CHART_SETTINGS: SavedChartSettings = {
  upColor: "#16a34a",
  downColor: "#dc2626",
  gridVisible: false,
  crosshairMode: 1,
  priceScalePosition: "right",
};

export const saveChartSettings = (settings: SavedChartSettings) => {
  try {
    localStorage.setItem(CHART_SETTINGS_KEY, JSON.stringify(settings));
  } catch (e) {
    console.warn("[Storage] Failed to save chart settings:", e);
  }
};

export const loadChartSettings = (): SavedChartSettings => {
  try {
    const raw = localStorage.getItem(CHART_SETTINGS_KEY);
    if (!raw) return DEFAULT_CHART_SETTINGS;
    const settings = { ...DEFAULT_CHART_SETTINGS, ...JSON.parse(raw) };
    // Migrate old default candle colors to the new slightly-darker values
    if (settings.upColor === "#22c55e") settings.upColor = "#16a34a";
    if (settings.downColor === "#ef4444") settings.downColor = "#dc2626";
    return settings;
  } catch (e) {
    return DEFAULT_CHART_SETTINGS;
  }
};

// ── Chart Template Persistence ──────────────────────────────────

export interface ChartTemplateIndicator {
  indicatorId: string;
  params: Record<string, any>;
  visible: boolean;
}

export interface ChartTemplate {
  id: string;
  name: string;
  isDefault: boolean;
  /** ChartType value: "candlestick" | "line" | "area" | "baseline" | "bar" */
  chartType: string;
  indicators: ChartTemplateIndicator[];
  createdAt: number;
  /** Number of bars visible when the template was saved */
  visibleBarsCount?: number;
}

const TEMPLATES_KEY = "marketview_chart_templates";

export const loadTemplates = (): ChartTemplate[] => {
  try {
    const raw = localStorage.getItem(TEMPLATES_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as ChartTemplate[];
  } catch (e) {
    return [];
  }
};

export const saveTemplate = (template: ChartTemplate): void => {
  try {
    const templates = loadTemplates();
    const idx = templates.findIndex(t => t.id === template.id);
    if (idx >= 0) {
      templates[idx] = template;
    } else {
      templates.push(template);
    }
    localStorage.setItem(TEMPLATES_KEY, JSON.stringify(templates));
  } catch (e) {
    console.warn("[Storage] Failed to save template:", e);
  }
};

export const deleteTemplate = (id: string): void => {
  try {
    const updated = loadTemplates().filter(t => t.id !== id);
    localStorage.setItem(TEMPLATES_KEY, JSON.stringify(updated));
  } catch (e) {
    console.warn("[Storage] Failed to delete template:", e);
  }
};

export const setDefaultTemplate = (id: string | null): void => {
  try {
    const updated = loadTemplates().map(t => ({ ...t, isDefault: t.id === id }));
    localStorage.setItem(TEMPLATES_KEY, JSON.stringify(updated));
  } catch (e) {
    console.warn("[Storage] Failed to set default template:", e);
  }
};

export const getDefaultTemplate = (): ChartTemplate | null => {
  return loadTemplates().find(t => t.isDefault) ?? null;
};

// ── Backfill History Days Setting ────────────────────────────────

const BACKFILL_DAYS_KEY = "marketview_backfill_days";

export const BACKFILL_DAYS_OPTIONS = [30, 60, 90, 180, 360] as const;
export type BackfillDays = (typeof BACKFILL_DAYS_OPTIONS)[number];

const DEFAULT_BACKFILL_DAYS: BackfillDays = 30;

export const saveBackfillDays = (days: BackfillDays): void => {
  try {
    localStorage.setItem(BACKFILL_DAYS_KEY, String(days));
  } catch (e) {
    console.warn("[Storage] Failed to save backfill days:", e);
  }
};

export const loadBackfillDays = (): BackfillDays => {
  try {
    const raw = localStorage.getItem(BACKFILL_DAYS_KEY);
    if (!raw) return DEFAULT_BACKFILL_DAYS;
    let val = parseInt(raw, 10);
    // Migrate old value: 365 was used before 360 was standardised
    if (val === 365) {
      val = 360;
      localStorage.setItem(BACKFILL_DAYS_KEY, "360");
    }
    return (BACKFILL_DAYS_OPTIONS as readonly number[]).includes(val)
      ? (val as BackfillDays)
      : DEFAULT_BACKFILL_DAYS;
  } catch {
    return DEFAULT_BACKFILL_DAYS;
  }
};

// ── EOD Backfill Duration Setting ───────────────────────────────

const EOD_DURATION_KEY = "marketview_eod_duration";

/** EOD backfill duration options in days (1Y, 5Y, 10Y, 20Y). */
export const EOD_DURATION_OPTIONS = [365, 1825, 3650, 7300] as const;
export type EodDuration = (typeof EOD_DURATION_OPTIONS)[number];

const DEFAULT_EOD_DURATION: EodDuration = 365;

export const saveEodDuration = (days: EodDuration): void => {
  try {
    localStorage.setItem(EOD_DURATION_KEY, String(days));
  } catch (e) {
    console.warn("[Storage] Failed to save EOD duration:", e);
  }
};

export const loadEodDuration = (): EodDuration => {
  try {
    const raw = localStorage.getItem(EOD_DURATION_KEY);
    if (!raw) return DEFAULT_EOD_DURATION;
    const val = parseInt(raw, 10);
    return (EOD_DURATION_OPTIONS as readonly number[]).includes(val)
      ? (val as EodDuration)
      : DEFAULT_EOD_DURATION;
  } catch {
    return DEFAULT_EOD_DURATION;
  }
};

// ── Chart Background Presets ─────────────────────────────────────

export interface ChartBgPreset {
  id: string;
  label: string;
  bg: string;
  textColor: string;
  gridColor: string;
  borderColor: string;
  category: "dark" | "light";
}

export const CHART_BG_PRESETS: ChartBgPreset[] = [
  // ── Dark backgrounds ──
  { id: "tv-classic",    label: "TV Classic",   bg: "#131722", textColor: "#d1d4dc", gridColor: "#2a2e39", borderColor: "#2a2e39", category: "dark"  },
  { id: "pure-black",    label: "Pure Black",   bg: "#0a0a0a", textColor: "#e0e0e0", gridColor: "#1c1c1c", borderColor: "#1c1c1c", category: "dark"  },
  { id: "midnight-navy", label: "Midnight Navy",bg: "#0d1117", textColor: "#c9d1d9", gridColor: "#21262d", borderColor: "#21262d", category: "dark"  },
  { id: "dark-slate",    label: "Dark Slate",   bg: "#1e1e2e", textColor: "#cdd6f4", gridColor: "#313244", borderColor: "#313244", category: "dark"  },
  { id: "dark-ocean",    label: "Dark Ocean",   bg: "#0f1923", textColor: "#c8d0dc", gridColor: "#1a2530", borderColor: "#1a2530", category: "dark"  },
  // ── Light backgrounds ──
  { id: "pure-white",    label: "Pure White",   bg: "#ffffff", textColor: "#191919", gridColor: "#e1e3eb", borderColor: "#d0d3db", category: "light" },
  { id: "off-white",     label: "Off White",    bg: "#fafafa", textColor: "#1a1a1a", gridColor: "#e0e0e0", borderColor: "#d0d0d0", category: "light" },
  { id: "light-blue",    label: "Light Blue",   bg: "#f0f4ff", textColor: "#1a2040", gridColor: "#d4dcf0", borderColor: "#c4cce4", category: "light" },
  { id: "warm-sepia",    label: "Warm Sepia",   bg: "#fdf6e3", textColor: "#2c1a0a", gridColor: "#ece0c0", borderColor: "#dcd0b0", category: "light" },
  { id: "soft-gray",     label: "Soft Gray",    bg: "#f5f5f5", textColor: "#1c1c1c", gridColor: "#e0e0e0", borderColor: "#cccccc", category: "light" },
];

const CHART_BG_KEY = "marketview_chart_bg";
export const DEFAULT_CHART_BG_ID = "tv-classic";

export const saveChartBg = (presetId: string): void => {
  try {
    localStorage.setItem(CHART_BG_KEY, presetId);
  } catch (e) {
    console.warn("[Storage] Failed to save chart bg:", e);
  }
};

export const loadChartBgId = (): string => {
  try {
    return localStorage.getItem(CHART_BG_KEY) || DEFAULT_CHART_BG_ID;
  } catch {
    return DEFAULT_CHART_BG_ID;
  }
};

export const loadChartBgPreset = (): ChartBgPreset => {
  const id = loadChartBgId();
  return CHART_BG_PRESETS.find(p => p.id === id) ?? CHART_BG_PRESETS[0];
};

/* ── Per-pane background overrides ── */
const PANE_BG_PREFIX = "marketview_pane_bg_";

export const savePaneBg = (panelId: string, presetId: string): void => {
  try {
    localStorage.setItem(PANE_BG_PREFIX + panelId, presetId);
  } catch (e) {
    console.warn("[Storage] Failed to save pane bg:", e);
  }
};

export const clearPaneBg = (panelId: string): void => {
  try {
    localStorage.removeItem(PANE_BG_PREFIX + panelId);
  } catch (e) {
    console.warn("[Storage] Failed to clear pane bg:", e);
  }
};

export const loadPaneBgId = (panelId: string): string | null => {
  try {
    return localStorage.getItem(PANE_BG_PREFIX + panelId);
  } catch {
    return null;
  }
};

export const loadPaneBgPreset = (panelId: string): ChartBgPreset | null => {
  const id = loadPaneBgId(panelId);
  if (!id) return null;
  return CHART_BG_PRESETS.find(p => p.id === id) ?? null;
};

// ── AmiBroker toggle ──────────────────────────────────────────────

const AMIBROKER_ENABLED_KEY = "amibroker_enabled";

export const getAmibrokerEnabled = (): boolean =>
  localStorage.getItem(AMIBROKER_ENABLED_KEY) === "true";

export const saveAmibrokerEnabled = (enabled: boolean): void => {
  if (enabled) {
    localStorage.setItem(AMIBROKER_ENABLED_KEY, "true");
  } else {
    localStorage.removeItem(AMIBROKER_ENABLED_KEY);
  }
};
